import { useEffect, useState, useRef } from 'react';
import {
  DollarSign,
  TrendingUp,
  AlertTriangle,
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  CheckCircle,
  XCircle,
} from 'lucide-react';

interface CombinedLocationBreakdown {
  locationId: string;
  locationName: string;
  ourRevenue: number;
  ourBatchCount: number;
  ghlAmount: number;
  ghlChargeCount: number;
  ghlError?: string;
}

interface RevenueData {
  ourData: {
    totalRevenue: number;
    totalCost: number;
    totalProfit: number;
    batchCount: number;
    totalContacts: number;
  };
  ghlData: {
    totalAmount: number;
    charges: any[];
    locationBreakdown: any[];
  } | null;
  combinedLocationBreakdown: CombinedLocationBreakdown[];
  discrepancy: {
    hasDiscrepancy: boolean;
    ourTotal: number;
    ghlTotal: number;
    difference: number;
  } | null;
  dateRange: {
    startDate: string;
    endDate: string;
  };
}

export default function AdminReports() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<RevenueData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [detailsData, setDetailsData] = useState<any>(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const fetchingDetailsRef = useRef(false); // Prevent duplicate fetches

  // Date range - default to current month
  const now = new Date();
  const firstOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
  const [startDate, setStartDate] = useState(firstOfMonth.toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(now.toISOString().split('T')[0]);

  useEffect(() => {
    fetchRevenueReport();
  }, [startDate, endDate]);

  const fetchRevenueReport = async () => {
    setLoading(true);
    setError(null);
    // Clear stale details data when date range changes (but keep showDetails state)
    setDetailsData(null);

    try {
      const params = new URLSearchParams({
        startDate,
        endDate,
      });

      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/reports/revenue?${params.toString()}`,
        { credentials: 'include' }
      );

      const result = await response.json() as any;

      if (result.success) {
        setData(result);
      } else {
        setError(result.error || 'Failed to fetch report');
      }
    } catch (err) {
      console.error('[AdminReports] Error:', err);
      setError('Failed to fetch revenue report');
    } finally {
      setLoading(false);
    }
  };

  // Internal function to fetch details data
  const fetchDetailsData = async () => {
    // Use ref to prevent duplicate fetches (more reliable than state during rapid renders)
    if (fetchingDetailsRef.current || loadingDetails) return;
    fetchingDetailsRef.current = true;

    setLoadingDetails(true);
    try {
      const params = new URLSearchParams({ startDate, endDate });
      const response = await fetch(
        `${import.meta.env.VITE_API_URL}/admin/reports/revenue/details?${params.toString()}`,
        { credentials: 'include' }
      );
      const result = await response.json() as any;
      if (result.success) {
        setDetailsData(result);
      }
    } catch (err) {
      console.error('[AdminReports] Details error:', err);
    } finally {
      setLoadingDetails(false);
      fetchingDetailsRef.current = false;
    }
  };

  // Toggle details visibility (and fetch if needed)
  const fetchDetails = async () => {
    if (detailsData) {
      // Data exists, just toggle visibility
      setShowDetails(!showDetails);
      return;
    }

    // No data, fetch and show
    setShowDetails(true);
    await fetchDetailsData();
  };

  // Auto-fetch details when showing but data is missing (e.g., after date range change)
  useEffect(() => {
    if (showDetails && !detailsData && !loadingDetails && data && !loading) {
      fetchDetailsData();
    }
  }, [showDetails, detailsData, loadingDetails, data, loading]);

  // Quick date range presets
  const setDatePreset = (preset: string) => {
    const today = new Date();
    let start: Date;

    switch (preset) {
      case 'today':
        start = today;
        break;
      case 'week':
        start = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        break;
      case 'quarter':
        const quarterMonth = Math.floor(today.getMonth() / 3) * 3;
        start = new Date(today.getFullYear(), quarterMonth, 1);
        break;
      case 'year':
        start = new Date(today.getFullYear(), 0, 1);
        break;
      default:
        start = new Date(today.getFullYear(), today.getMonth(), 1);
    }

    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(today.toISOString().split('T')[0]);
    setDetailsData(null);
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Date Range Controls */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-4">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex items-center space-x-2">
            <Calendar className="w-5 h-5 text-gray-400" />
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                const newStart = e.target.value;
                setStartDate(newStart);
                // If start date moved after end date, move end date to match
                if (newStart > endDate) {
                  setEndDate(newStart);
                }
                setDetailsData(null);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
            <span className="text-gray-500">to</span>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                const newEnd = e.target.value;
                setEndDate(newEnd);
                // If end date moved before start date, move start date to match
                if (newEnd < startDate) {
                  setStartDate(newEnd);
                }
                setDetailsData(null);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Quick Presets */}
          <div className="flex flex-wrap gap-2">
            {['today', 'week', 'month', 'quarter', 'year'].map((preset) => (
              <button
                key={preset}
                onClick={() => setDatePreset(preset)}
                className="px-3 py-1 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg capitalize"
              >
                {preset === 'week' ? 'Last 7 Days' : preset === 'month' ? 'This Month' : preset}
              </button>
            ))}
          </div>

          <button
            onClick={fetchRevenueReport}
            disabled={loading}
            className="ml-auto p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
          >
            <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard
              title="Total Revenue"
              value={`$${data.ourData.totalRevenue.toFixed(2)}`}
              icon={DollarSign}
              iconBg="bg-green-100"
              iconColor="text-green-600"
            />
            <StatCard
              title="Total Cost"
              value={`$${data.ourData.totalCost.toFixed(2)}`}
              icon={DollarSign}
              iconBg="bg-red-100"
              iconColor="text-red-600"
            />
            <StatCard
              title="Total Profit"
              value={`$${data.ourData.totalProfit.toFixed(2)}`}
              icon={TrendingUp}
              iconBg="bg-emerald-100"
              iconColor="text-emerald-600"
            />
            <StatCard
              title="Batches / Contacts"
              value={`${data.ourData.batchCount} / ${data.ourData.totalContacts}`}
              subtitle="batches / contacts"
              icon={Calendar}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
            />
          </div>

          {/* GHL Comparison */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">GHL Billing Comparison</h2>
            </div>

            <div className="p-6">
              {data.ghlData ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div>
                      <p className="text-sm text-gray-500">Our Records</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${data.ourData.totalRevenue.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">GHL Charges</p>
                      <p className="text-2xl font-bold text-gray-900">
                        ${data.ghlData.totalAmount.toFixed(2)}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Status</p>
                      {data.discrepancy?.hasDiscrepancy ? (
                        <div className="flex items-center text-red-600">
                          <XCircle className="w-5 h-5 mr-2" />
                          <span className="text-lg font-semibold">
                            Difference: ${Math.abs(data.discrepancy.difference).toFixed(2)}
                          </span>
                        </div>
                      ) : (
                        <div className="flex items-center text-green-600">
                          <CheckCircle className="w-5 h-5 mr-2" />
                          <span className="text-lg font-semibold">Matched</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Location Breakdown - Combined Our Revenue + GHL Revenue */}
                  {data.combinedLocationBreakdown && data.combinedLocationBreakdown.length > 0 && (
                    <div className="mt-4 border-t border-gray-200 pt-4">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        Revenue by Location ({data.combinedLocationBreakdown.length} locations)
                      </h4>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left">Location</th>
                              <th className="px-3 py-2 text-right">Our Batches</th>
                              <th className="px-3 py-2 text-right">Our Revenue</th>
                              <th className="px-3 py-2 text-right">GHL Charges</th>
                              <th className="px-3 py-2 text-right">GHL Amount</th>
                              <th className="px-3 py-2 text-left">Status</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {data.combinedLocationBreakdown.map((loc) => (
                              <tr key={loc.locationId}>
                                <td className="px-3 py-2">
                                  <div className="font-medium">{loc.locationName}</div>
                                  <div className="text-xs text-gray-500 font-mono">{loc.locationId}</div>
                                </td>
                                <td className="px-3 py-2 text-right">{loc.ourBatchCount}</td>
                                <td className="px-3 py-2 text-right font-medium">${loc.ourRevenue.toFixed(2)}</td>
                                <td className="px-3 py-2 text-right">{loc.ghlChargeCount}</td>
                                <td className="px-3 py-2 text-right">${loc.ghlAmount.toFixed(2)}</td>
                                <td className="px-3 py-2">
                                  {loc.ghlError ? (
                                    <span className="text-red-600 text-xs">{loc.ghlError}</span>
                                  ) : Math.abs(loc.ourRevenue - loc.ghlAmount) < 0.01 ? (
                                    <span className="text-green-600 text-xs">Match</span>
                                  ) : (
                                    <span className="text-yellow-600 text-xs">
                                      Diff: ${Math.abs(loc.ourRevenue - loc.ghlAmount).toFixed(2)}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Show details button if discrepancy */}
                  {data.discrepancy?.hasDiscrepancy && (
                    <button
                      onClick={fetchDetails}
                      disabled={loadingDetails}
                      className="flex items-center text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                      {loadingDetails ? (
                        <RefreshCw className="w-4 h-4 mr-1 animate-spin" />
                      ) : showDetails ? (
                        <ChevronUp className="w-4 h-4 mr-1" />
                      ) : (
                        <ChevronDown className="w-4 h-4 mr-1" />
                      )}
                      {showDetails ? 'Hide Details' : 'View Details'}
                    </button>
                  )}

                  {/* Details Table */}
                  {showDetails && detailsData && (
                    <div className="mt-4 space-y-4">
                      {/* Unmatched from our side */}
                      {detailsData.unmatched?.ours?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            In Our Records, Not in GHL ({detailsData.unmatched.ours.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left">Batch</th>
                                  <th className="px-3 py-2 text-left">Transaction ID</th>
                                  <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {detailsData.unmatched.ours.map((batch: any) => (
                                  <tr key={batch.id}>
                                    <td className="px-3 py-2">{batch.batch_name}</td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                      {batch.payment_transaction_id || 'N/A'}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      ${parseFloat(batch.actual_revenue || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Unmatched from GHL side */}
                      {detailsData.unmatched?.ghl?.length > 0 && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-2">
                            In GHL, Not in Our Records ({detailsData.unmatched.ghl.length})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="min-w-full text-sm">
                              <thead className="bg-gray-50">
                                <tr>
                                  <th className="px-3 py-2 text-left">Location</th>
                                  <th className="px-3 py-2 text-left">Charge ID</th>
                                  <th className="px-3 py-2 text-left">Description</th>
                                  <th className="px-3 py-2 text-right">Amount</th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-gray-100">
                                {detailsData.unmatched.ghl.map((charge: any) => (
                                  <tr key={charge.chargeId}>
                                    <td className="px-3 py-2">
                                      <div className="font-medium">{charge.locationName || 'Unknown'}</div>
                                      <div className="text-xs text-gray-500 font-mono">{charge.locationId}</div>
                                    </td>
                                    <td className="px-3 py-2 font-mono text-xs">
                                      {charge.chargeId}
                                    </td>
                                    <td className="px-3 py-2">
                                      {charge.meta?.description || 'N/A'}
                                    </td>
                                    <td className="px-3 py-2 text-right">
                                      ${(charge.amountCharged || 0).toFixed(2)}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center text-yellow-600">
                  <AlertTriangle className="w-5 h-5 mr-2" />
                  <span>Unable to fetch GHL billing data. This could mean no batches have locations with OAuth tokens in this date range, or the billing API is unavailable.</span>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// Stat Card
function StatCard({
  title,
  value,
  subtitle,
  icon: Icon,
  iconBg,
  iconColor,
}: {
  title: string;
  value: string;
  subtitle?: string;
  icon: typeof DollarSign;
  iconBg: string;
  iconColor: string;
}) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-xs text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`${iconBg} p-3 rounded-xl`}>
          <Icon className={`w-5 h-5 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}
